/**
 * Utility functions for handling browser cookies
 * @module cookieUtils
 */

/**
 * Retrieves the value of a cookie by its name
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string|null} The cookie value if found, null otherwise
 * 
 * @example
 * const value = getCookie('user_id');
 * if (value) {
 *   console.log('User ID:', value);
 * }
 */
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop().split(';').shift();
    }
    return null;
};

/**
 * Sets a cookie with the given name, value, and options
 * @param {string} name - The name of the cookie
 * @param {string} value - The value to store in the cookie
 * @param {Object} [options] - Cookie options
 * @param {number} [options.days] - Number of days until cookie expires
 * @param {string} [options.path='/'] - Cookie path
 * @param {string} [options.domain] - Cookie domain
 * @param {boolean} [options.secure=false] - Whether cookie requires HTTPS
 * @param {string} [options.sameSite='Lax'] - SameSite attribute value
 * 
 * @example
 * // Simple usage
 * setCookie('user_id', '123', { days: 30 });
 * 
 * // With more options
 * setCookie('session', 'abc123', {
 *   days: 7,
 *   secure: true,
 *   sameSite: 'Strict'
 * });
 */
const setCookie = (name, value, options = {}) => {
    const {
        days = null,
        path = '/',
        domain = '',
        secure = false,
        sameSite = 'Lax'
    } = options;

    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        cookie += `; expires=${date.toUTCString()}`;
    }

    if (path) cookie += `; path=${path}`;
    if (domain) cookie += `; domain=${domain}`;
    if (secure) cookie += '; secure';
    if (sameSite) cookie += `; samesite=${sameSite}`;

    document.cookie = cookie;
};

export { getCookie, setCookie };